import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { meetingAPI } from '../utils/api';
import MeetingRoomView from '../components/MeetingRoomView';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Video, Lock, Loader2, Users, Crown } from 'lucide-react';

const JoinMeetingPage = () => {
  const { joinCode: urlCode } = useParams();
  const { user, socket } = useAuth();
  const navigate = useNavigate();

  // States
  const [codeInput, setCodeInput] = useState('');
  const [meeting, setMeeting] = useState(null);       // Preview info (from URL code)
  const [joining, setJoining] = useState(false);
  const [joined, setJoined] = useState(false);
  const [verifying, setVerifying] = useState(!!urlCode); // Auto-verify if URL has code
  const [showManualEntry, setShowManualEntry] = useState(!urlCode);

  // If URL has a code, auto-verify it on load to show meeting info
  useEffect(() => {
    if (!urlCode) return;
    const verify = async () => {
      try {
        const res = await meetingAPI.getMeetingByCode(urlCode);
        setMeeting(res.data);
      } catch (e) {
        // Code invalid — fall back to manual entry
        setShowManualEntry(true);
        setCodeInput(urlCode);
      } finally {
        setVerifying(false);
      }
    };
    verify();
  }, [urlCode]);

  // "Tap to Join" — uses the URL code directly
  const handleTapToJoin = async () => {
    setJoining(true);
    try {
      const res = await meetingAPI.joinMeeting(urlCode);
      setMeeting(res.data);
      setJoined(true);
    } catch (e) {
      const detail = e.response?.data?.detail || 'Failed to join meeting';
      toast.error(detail);
    } finally {
      setJoining(false);
    }
  };

  // Manual code entry join
  const handleJoinWithCode = async () => {
    let code = codeInput.trim().toLowerCase();
    if (!code) { toast.error('Enter the meeting code'); return; }
    const urlMatch = code.match(/\/join\/([a-z0-9-]+)/);
    if (urlMatch) code = urlMatch[1];
    code = code.replace(/^https?:\/\/.*\//, '').trim();

    setJoining(true);
    try {
      const res = await meetingAPI.joinMeeting(code);
      setMeeting(res.data);
      setJoined(true);
    } catch (e) {
      const detail = e.response?.data?.detail || 'Invalid code or meeting has ended';
      toast.error(detail);
    } finally {
      setJoining(false);
    }
  };

  // === JOINED → Show meeting room ===
  if (joined && meeting) {
    return (
      <MeetingRoomView
        meeting={meeting}
        socket={socket}
        onLeave={() => navigate('/')}
      />
    );
  }

  // === VERIFYING (loading) ===
  if (verifying) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
        <div className="text-center space-y-3">
          <Loader2 className="w-8 h-8 text-[#4338CA] animate-spin mx-auto" />
          <p className="text-white/50 text-sm">Finding meeting...</p>
        </div>
      </div>
    );
  }

  // === URL CODE VERIFIED → Show "Tap to Join" ===
  if (meeting && !showManualEntry) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center px-6" data-testid="join-meeting-page">
        <div className="w-full max-w-sm space-y-6">
          {/* Meeting info */}
          <div className="bg-[#1E293B] rounded-2xl p-6 border border-white/10 space-y-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-[#4338CA]/20 flex items-center justify-center shrink-0">
                <Video className="w-6 h-6 text-[#4338CA]" />
              </div>
              <div className="min-w-0">
                <h1 className="text-xl font-bold text-white truncate" data-testid="join-meeting-title">{meeting.title}</h1>
                <div className="flex items-center gap-1 text-white/50 text-sm">
                  <Crown className="w-3 h-3" />
                  <span className="truncate">Hosted by {meeting.host_name || 'Unknown'}</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4 text-white/40 text-sm">
              <span className="flex items-center gap-1.5">
                <Users className="w-4 h-4" />
                {meeting.participants?.length || 0} in room
              </span>
              <span className="flex items-center gap-1.5">
                <div className={`w-2 h-2 rounded-full ${meeting.status === 'live' ? 'bg-green-500 animate-pulse' : 'bg-amber-500'}`} />
                {meeting.status === 'live' ? 'Live' : 'Waiting'}
              </span>
            </div>

            {/* Big Tap to Join button */}
            <Button
              onClick={handleTapToJoin}
              disabled={joining}
              className="w-full py-5 text-lg bg-[#4338CA] hover:bg-[#3730A3] text-white rounded-xl font-semibold shadow-lg shadow-[#4338CA]/25 active:scale-[0.98] transition-all"
              data-testid="confirm-join-btn"
            >
              {joining ? (
                <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Joining...</>
              ) : (
                'Tap to Join'
              )}
            </Button>
          </div>

          {/* User info */}
          <div className="text-center text-white/30 text-xs">
            Joining as <span className="text-white/60 font-medium">{user?.name}</span>
          </div>

          {/* Enter code manually link */}
          <div className="text-center">
            <button onClick={() => { setShowManualEntry(true); setMeeting(null); }} className="text-white/30 hover:text-white/50 text-xs transition-colors" data-testid="enter-code-manually">
              Enter code manually instead
            </button>
          </div>
        </div>
      </div>
    );
  }

  // === MANUAL CODE ENTRY (no URL code or invalid code) ===
  return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center px-6" data-testid="join-meeting-page">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-[#4338CA]/20 mx-auto flex items-center justify-center mb-4">
            <Lock className="w-7 h-7 text-[#4338CA]" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-1">Join Meeting</h1>
          <p className="text-white/50 text-sm">Enter the meeting code to join</p>
        </div>

        <div className="bg-[#1E293B] rounded-2xl p-6 border border-white/10 space-y-4">
          <div>
            <Label className="text-white/70">Meeting Code</Label>
            <Input
              value={codeInput}
              onChange={(e) => setCodeInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleJoinWithCode()}
              placeholder="e.g. abc-defg or paste link"
              className="mt-1 bg-[#0F172A] border-white/20 text-white text-center text-lg font-mono tracking-widest placeholder:text-white/30 placeholder:text-sm placeholder:tracking-normal"
              autoFocus
              data-testid="join-code-input"
            />
          </div>

          <Button
            onClick={handleJoinWithCode}
            disabled={joining || !codeInput.trim()}
            className="w-full py-4 text-base bg-[#4338CA] hover:bg-[#3730A3] text-white rounded-xl font-semibold"
            data-testid="confirm-join-btn"
          >
            {joining ? (
              <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Verifying...</>
            ) : (
              'Join Meeting'
            )}
          </Button>
        </div>

        <div className="text-center text-white/30 text-xs">
          Joining as <span className="text-white/60">{user?.name}</span>
        </div>

        <div className="text-center">
          <button onClick={() => navigate('/')} className="text-white/40 hover:text-white/60 text-sm transition-colors" data-testid="back-home-btn">
            Back to CallChat
          </button>
        </div>
      </div>
    </div>
  );
};

export default JoinMeetingPage;
